import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function TreatsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  console.log("TreatsGridConsumer Render:", {
    products,
    loading,
    error
  });
  try {
    if (loading) {
      return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
          {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="space-y-4">
              <div className="aspect-square rounded-lg bg-muted animate-pulse" />
              <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
              <div className="h-6 w-3/4 bg-muted animate-pulse rounded-md" />
            </div>)}
        </div>;
    }
    if (error) {
      return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 flex items-center justify-between">
          <div className="text-sm">
            Unable to load travel packages: {error}
          </div>
          <Button variant="link" onClick={refetch} className="p-0 h-auto underline text-red-800 hover:text-red-900">
            Retry
          </Button>
        </div>;
    }
    if (products.length === 0) {
      return <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          <p className="font-sans italic text-lg">
            Our travel packages are being curated.
          </p>
        </div>;
    }
    const formatIndex = (i: number) => String(i + 1).padStart(2, "0");
    return <div className="relative">
        {}
        <div className="mb-4 flex gap-4 text-[10px] font-mono uppercase tracking-widest text-muted-foreground/50">
          <span>loading={String(loading)}</span>
          <span>error={error ? error : 'none'}</span>
          <span>products count={products.length}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-16">
          {products.map((product, i) => {
          const categoryName = product.categories && product.categories.length > 0 ? product.categories[0].name : "PACKAGE";
          return <article data-index={i} key={product.id} className="group relative flex flex-col">
                <div className="absolute -top-2 -left-1 z-10 font-mono text-xs tracking-widest text-muted-foreground">
                  {formatIndex(i)}
                </div>

                <Link to={product.link} className="block">
                  <div className="relative aspect-square overflow-hidden rounded-lg bg-card shadow-md">
                    {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />}
                  </div>
                </Link>

                <div className="mt-6 flex flex-col">
                  <span className="font-default text-[11px] tracking-[0.2em] text-muted-foreground uppercase">
                    — {categoryName}
                  </span>

                  <div className="mt-2 flex items-start justify-between gap-4">
                    <Link to={product.link} className="block flex-1">
                      <h3 className="font-default font-medium text-[22px] leading-tight text-foreground">
                        {product.title}
                      </h3>
                    </Link>
                    <span className="font-default text-base text-foreground shrink-0 pt-1">
                      R{typeof product.price === "number" ? product.price.toFixed(2) : product.price}
                    </span>
                  </div>

                  <div className="mt-5 relative">
                    <AddToCartButton productId={product.id} variant="link" className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-lg" text="Book Now" showQuantity={false} />
                  </div>
                </div>
              </article>;
        })}
        </div>
      </div>;
  } catch (err) {
    return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="text-sm font-medium">A critical error occurred while rendering the travel packages.</p>
        <p className="text-xs mt-1 opacity-70">{err instanceof Error ? err.message : "Unknown error"}</p>
      </div>;
  }
}
export default function TravelHighlights() {
  const wp_query_params = {
    per_page: 4,
    post_type: "product"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2202" id="populargetawayshero" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 lg:mb-28">
          <div className="lg:col-span-7">
            <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase mb-8">
              02 — Featured Getaways
            </p>
            <h2 className="font-default font-light text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] leading-[1.05] text-foreground tracking-tight">
              Travel experiences, curated{" "}
              <em className="italic font-normal text-primary">
                for your next
              </em>{" "}
              adventure.
            </h2>
          </div>

          <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-end">
            <p className="font-default text-base lg:text-lg leading-relaxed text-muted-foreground mb-8">
              Hand-picked destinations, seamless itineraries, and exclusive access. 
              Our travel packages are designed for the discerning explorer — 
              never compromised, always unforgettable.
            </p>
            <Link to="/DestinationsPage" className="group inline-flex items-center gap-2 font-default text-xs tracking-[0.2em] uppercase text-foreground border-b border-foreground/40 pb-2 w-fit hover:border-foreground transition-colors">
              <span>View All Destinations</span>
              <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 4,
        post_type: "product"
      }}>
          <TreatsGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}